import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Search, X, Truck, Mail, Phone, Bike, MapPin, Clock, CircleDot
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Berhampur center as default fallback
const DEFAULT_CENTER = [19.3149, 84.7941];

// Build a coloured scooter/bike SVG marker (different colour for online vs offline)
const buildIcon = (isOnline) => {
  const ringColor = isOnline ? '#10B981' : '#9CA3AF';
  const fillColor = isOnline ? '#059669' : '#6B7280';
  const html = `
    <div style="
      position: relative;
      width: 44px;
      height: 44px;
      transform: translate(-50%, -100%);
    ">
      <div style="
        position: absolute;
        inset: 0;
        background: white;
        border: 3px solid ${ringColor};
        border-radius: 50%;
        box-shadow: 0 4px 10px rgba(0,0,0,.18);
        display:flex; align-items:center; justify-content:center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${fillColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/>
          <path d="M12 17.5V14l-3-3 4-3 2 3h2"/>
        </svg>
      </div>
      ${isOnline ? `
        <span style="
          position:absolute; top:0; right:0;
          width:12px; height:12px; border-radius:50%;
          background:#10B981; border:2px solid white;
          animation: foodk-pulse 1.6s infinite;
        "></span>` : ''}
    </div>
    <style>
      @keyframes foodk-pulse {
        0% { box-shadow: 0 0 0 0 rgba(16,185,129,.6); }
        70%{ box-shadow: 0 0 0 8px rgba(16,185,129,0); }
        100%{ box-shadow: 0 0 0 0 rgba(16,185,129,0); }
      }
    </style>
  `;
  return L.divIcon({
    html,
    className: 'foodk-agent-marker',
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -42]
  });
};

const formatLastActive = (d) => {
  if (!d) return 'unknown';
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return format(new Date(d), 'dd MMM yyyy hh:mm a');
};

const AgentLocations = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | online | offline
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/delivery-agents`);
        setAgents(res.data || []);
      } catch (err) {
        toast.error('Failed to load agents');
      } finally {
        setLoading(false);
      }
    };
    load();
    // Refresh every 30s for live tracking
    timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  const isOnline = (a) => a.workStatus === 1 || a.workStatus === true;

  const withLocation = useMemo(
    () => agents.filter((a) => Number.isFinite(Number(a.latitude)) && Number.isFinite(Number(a.longitude)) && a.latitude !== 0 && a.longitude !== 0),
    [agents]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withLocation.filter((a) => {
      if (statusFilter === 'online' && !isOnline(a)) return false;
      if (statusFilter === 'offline' && isOnline(a)) return false;
      if (q) {
        const hay = [a.name, a.phone, a.email, a.vehicleType, a.vehicleNo, a.zone?.name]
          .filter(Boolean).map((v) => String(v).toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [withLocation, search, statusFilter]);

  const onlineCount = withLocation.filter(isOnline).length;
  const offlineCount = withLocation.length - onlineCount;

  const center = filtered.length
    ? [Number(filtered[0].latitude), Number(filtered[0].longitude)]
    : DEFAULT_CENTER;

  const selected = filtered.find((a) => a.id === selectedId) || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-0px)] flex" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      {/* LEFT SIDEBAR */}
      <aside className="w-[340px] border-r border-gray-200 bg-white flex flex-col h-screen" data-testid="agent-locations-sidebar">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={20} className="text-orange-600" />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
              Agent Locations
            </h1>
          </div>
          <p className="text-xs text-gray-500">Live tracking of delivery agents on the map</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
            <div className="bg-orange-50 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold text-orange-700">{withLocation.length}</p>
              <p className="text-[10px] text-gray-600 uppercase">Tracked</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold text-emerald-700">{onlineCount}</p>
              <p className="text-[10px] text-gray-600 uppercase">Online</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold text-gray-700">{offlineCount}</p>
              <p className="text-[10px] text-gray-600 uppercase">Offline</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="agent-loc-search"
              placeholder="Search name, phone, email..."
              className="pl-8 pr-8 h-9 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Status pills */}
          <div className="flex gap-1.5 mt-3">
            {[
              { id: 'all', label: 'All' },
              { id: 'online', label: 'Online' },
              { id: 'offline', label: 'Offline' }
            ].map((p) => (
              <button
                key={p.id}
                data-testid={`agent-loc-pill-${p.id}`}
                onClick={() => setStatusFilter(p.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === p.id
                    ? 'bg-orange-50 border-orange-300 text-orange-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* AGENT LIST */}
        <div className="flex-1 overflow-y-auto" data-testid="agent-loc-list">
          {filtered.length === 0 ? (
            <div className="text-center py-10 px-6 text-sm text-gray-500">
              {withLocation.length === 0
                ? 'No agents have shared their location yet.'
                : 'No agents match your filters.'}
            </div>
          ) : (
            filtered.map((a) => {
              const online = isOnline(a);
              const isActive = a.id === selectedId;
              return (
                <button
                  key={a.id}
                  data-testid={`agent-loc-item-${a.id}`}
                  onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left px-5 py-3 border-b border-gray-100 hover:bg-orange-50/40 transition-colors flex gap-3 ${
                    isActive ? 'bg-orange-50' : ''
                  }`}
                >
                  <div className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center ${
                    online ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <Bike size={18} className={online ? 'text-emerald-700' : 'text-gray-500'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">{a.name || a.phone || 'Unnamed'}</p>
                      <CircleDot size={10} className={online ? 'text-emerald-500' : 'text-gray-400'} />
                    </div>
                    <p className="text-xs text-gray-500 truncate">{a.phone || '-'}</p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {a.zone?.name && (
                        <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px] px-1.5 py-0">
                          {a.zone.name}
                        </Badge>
                      )}
                      <span className="text-[10px] text-gray-500 flex items-center gap-1">
                        <Clock size={10} />{formatLastActive(a.lastActiveAt || a.updatedAt)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* MAP */}
      <div className="flex-1 relative">
        <MapContainer
          center={center}
          zoom={13}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom
          data-testid="agent-loc-map"
        >
          <TileLayer
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {filtered.map((a) => {
            const online = isOnline(a);
            return (
              <Marker
                key={a.id}
                position={[Number(a.latitude), Number(a.longitude)]}
                icon={buildIcon(online)}
                eventHandlers={{ click: () => setSelectedId(a.id) }}
              >
                <Popup>
                  <div className="min-w-[220px]" data-testid={`agent-loc-popup-${a.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${online ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                        <Bike size={18} className={online ? 'text-emerald-700' : 'text-gray-500'} />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm leading-tight">
                          {a.name || 'Unnamed'}
                        </p>
                        <Badge className={`${online ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'} border-0 text-[10px] px-1.5 py-0 mt-0.5`}>
                          {online ? 'Online' : 'Offline'}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-1 text-sm">
                      {a.phone && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone size={13} className="text-gray-400" />
                          <a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a>
                        </div>
                      )}
                      {a.email && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail size={13} className="text-gray-400" />
                          <a href={`mailto:${a.email}`} className="hover:underline truncate">{a.email}</a>
                        </div>
                      )}
                      {a.vehicleType && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Truck size={13} className="text-gray-400" />
                          <span>{a.vehicleType}{a.vehicleNo ? ` · ${a.vehicleNo}` : ''}</span>
                        </div>
                      )}
                      {a.zone?.name && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <MapPin size={13} className="text-gray-400" />
                          <span>{a.zone.name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-500 text-xs pt-1 border-t mt-2">
                        <Clock size={11} />
                        <span>Last active: {formatLastActive(a.lastActiveAt || a.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Floating selected agent banner (mobile fallback) */}
        {selected && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white border shadow-lg rounded-lg px-4 py-2 flex items-center gap-3 lg:hidden">
            <Bike size={16} className="text-emerald-700" />
            <span className="text-sm font-medium">{selected.name}</span>
            <Button size="sm" variant="ghost" onClick={() => setSelectedId(null)}>
              <X size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentLocations;
